import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../lib/api';
import { Icons } from '../components/icons';

const emptyProbe = { host: '', port: 1883, username: '', password: '', topic: 'gridorbit/ops/probe' };

export function MosquittoOps() {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [probe, setProbe] = useState(emptyProbe);
  const [probeResult, setProbeResult] = useState(null);
  const [probing, setProbing] = useState(false);
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({ username: '', password: '' });
  const [acl, setAcl] = useState('');
  const [logs, setLogs] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState('');

  const loadOverview = useCallback(async () => {
    setLoading(true);
    setMsg('');
    const d = await apiFetch('/api/v1/platform/mqtt-ops/overview');
    if (d.code === 0 && d.data) {
      setOverview(d.data);
      const sm = d.data.station_mqtt || {};
      setProbe(p => ({
        ...p,
        host: sm.host || p.host,
        port: sm.port || 1883,
        username: sm.username || p.username,
        // 密码不从 overview 回显；若平台有密，探测时后端会补全
      }));
    } else {
      setMsg(d.message || '加载失败');
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadOverview(); }, [loadOverview]);

  const fillFromPlatform = () => {
    const sm = overview?.station_mqtt || {};
    setProbe(p => ({
      ...p,
      host: sm.host || '',
      port: sm.port || 1883,
      username: sm.username || '',
      password: '',
    }));
    setMsg(overview?.station_mqtt_has_password
      ? '已从平台填入地址/用户；密码由后端探测时自动使用已保存值（留空即可）'
      : '已从平台填入；平台未存密码，请手动填写');
  };

  const syncToPlatform = async () => {
    if (!probe.host.trim()) { setMsg('请填写 Broker 地址'); return; }
    setBusy('sync');
    const body = {
      host: probe.host.trim(),
      port: parseInt(probe.port, 10) || 1883,
      username: probe.username,
      password: probe.password,
    };
    const d = await apiFetch('/api/v1/platform/mqtt-ops/sync-to-platform', { method: 'POST', body });
    setBusy('');
    setMsg(d.code === 0 ? (d.message || '已同步') : (d.message || '同步失败'));
    if (d.code === 0) loadOverview();
  };

  const runProbe = async (useAgent) => {
    setProbing(true);
    setProbeResult(null);
    const body = {
      host: probe.host.trim(),
      port: parseInt(probe.port, 10) || 1883,
      username: probe.username,
      password: probe.password,
      topic: probe.topic || 'gridorbit/ops/probe',
      use_agent: !!useAgent,
    };
    const d = await apiFetch('/api/v1/platform/mqtt-ops/test-connect', { method: 'POST', body });
    setProbing(false);
    if (d.code === 0 && d.data) {
      setProbeResult(d.data);
    } else {
      setProbeResult({ ok: false, message: d.message || '探测失败', detail: '' });
    }
  };

  const loadUsers = async () => {
    setBusy('users');
    const d = await apiFetch('/api/v1/platform/mqtt-ops/users');
    setBusy('');
    if (d.code === 0 && d.data?.users) {
      setUsers(d.data.users);
      setMsg(`已加载 ${d.data.users.length} 个 MQTT 用户（${d.data.passwd_file || ''}）`);
    } else {
      setMsg(d.message || '无法加载用户（需 Agent）');
      setUsers([]);
    }
  };

  const saveUser = async () => {
    if (!newUser.username.trim() || !newUser.password) {
      setMsg('请填写用户名和密码');
      return;
    }
    setBusy('saveUser');
    const d = await apiFetch('/api/v1/platform/mqtt-ops/users', {
      method: 'POST',
      body: { username: newUser.username.trim(), password: newUser.password },
    });
    setBusy('');
    setMsg(d.message || (d.code === 0 ? '已保存' : '失败'));
    if (d.code === 0) {
      setNewUser({ username: '', password: '' });
      loadUsers();
    }
  };

  const loadAcl = async () => {
    setBusy('acl');
    const d = await apiFetch('/api/v1/platform/mqtt-ops/acl');
    setBusy('');
    if (d.code === 0 && d.data) {
      setAcl(d.data.content || '');
      setMsg(`已加载 ACL：${d.data.acl_file || ''}`);
    } else {
      setMsg(d.message || '无法加载 ACL');
    }
  };

  const saveAcl = async () => {
    if (!acl.trim()) { setMsg('ACL 不能为空'); return; }
    setBusy('saveAcl');
    const d = await apiFetch('/api/v1/platform/mqtt-ops/acl', {
      method: 'PUT',
      body: { content: acl, reload: true },
    });
    setBusy('');
    setMsg(d.message || (d.code === 0 ? 'ACL 已保存并 reload' : '保存失败'));
  };

  const loadLogs = async () => {
    setBusy('logs');
    const d = await apiFetch('/api/v1/platform/mqtt-ops/agent/logs?lines=80');
    setBusy('');
    if (d.code === 0 && d.data) {
      setLogs(d.data.content || '');
    } else {
      setMsg(d.message || '无法读取日志');
    }
  };

  const reloadMosquitto = async () => {
    setBusy('reload');
    const d = await apiFetch('/api/v1/platform/mqtt-ops/reload', { method: 'POST', body: {} });
    setBusy('');
    setMsg(d.message || (d.code === 0 ? 'reload 完成' : 'reload 失败'));
  };

  const agent = overview?.agent || {};
  const agentData = agent.data || {};
  const topics = overview?.topics || [];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, gap: 12 }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 650 }}>Mosquitto 运维</h2>
          <p style={{ fontSize: 13, color: 'var(--brand-text-muted)', marginTop: 4 }}>
            Broker 配置运维与连通探测（非自研 MQTT 引擎）。工位下发配置仍走「工位终端」页的 station_mqtt。
          </p>
        </div>
        <button className="btn btn-outline" onClick={loadOverview} disabled={loading}>
          <Icons.Refresh /> 刷新
        </button>
      </div>

      {msg && (
        <div className={msg.includes('失败') || msg.includes('无法') ? 'error' : 'lc-badge'} style={{ marginBottom: 12 }}>
          {msg}
        </div>
      )}

      {loading ? <div className="empty"><span className="spinner" /></div> : (
        <>
          {/* Broker / Agent 概况 */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginBottom: 16 }}>
            <div className="card">
              <div style={{ fontWeight: 600, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Icons.CloudServer /> 平台 station_mqtt
              </div>
              <div style={{ fontSize: 13, lineHeight: 1.7, color: 'var(--brand-text-muted)' }}>
                <div>Host：{overview?.station_mqtt?.host || '—'}</div>
                <div>Port：{overview?.station_mqtt?.port || '—'}</div>
                <div>User：{overview?.station_mqtt?.username || '—'}</div>
                <div>密码：{overview?.station_mqtt_has_password ? '已配置（不回显）' : '未配置'}</div>
              </div>
              <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button className="btn btn-outline btn-xs" onClick={fillFromPlatform}>从平台填入探测</button>
                <button className="btn btn-outline btn-xs" onClick={syncToPlatform} disabled={busy === 'sync'}>
                  {busy === 'sync' ? '同步中…' : '同步到平台'}
                </button>
              </div>
            </div>

            <div className="card">
              <div style={{ fontWeight: 600, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Icons.Monitor /> Agent / Mosquitto
              </div>
              {!agent.available ? (
                <div style={{ fontSize: 13, color: 'var(--brand-text-muted)' }}>
                  {agent.stub
                    ? 'Agent 未配置（MQTT_OPS_AGENT_URL）。平台侧连通探测仍可用；用户/ACL/status 待部署 Agent。'
                    : `Agent 不可用：${agent.error || '未知错误'}`}
                </div>
              ) : (
                <div style={{ fontSize: 13, lineHeight: 1.7, color: 'var(--brand-text-muted)' }}>
                  <div>
                    服务状态：
                    <span style={{ color: agentData.service_active ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
                      {agentData.systemctl_active || '—'}
                    </span>
                  </div>
                  <div>匿名：{agentData.allow_anonymous ? '允许（不安全）' : '已关闭'}</div>
                  <div>passwd：{agentData.passwd_file || '—'}</div>
                  <div>acl：{agentData.acl_file || '—'}</div>
                  <div>conf：{agentData.conf_file || '—'}</div>
                </div>
              )}
              <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button className="btn btn-outline btn-xs" onClick={reloadMosquitto} disabled={busy === 'reload'}>
                  {busy === 'reload' ? 'reload…' : 'Reload Mosquitto'}
                </button>
                <button className="btn btn-outline btn-xs" onClick={loadLogs} disabled={busy === 'logs'}>
                  拉取日志
                </button>
              </div>
            </div>
          </div>

          {/* 连通性测试 */}
          <div className="card" style={{ marginBottom: 16, maxWidth: 720 }}>
            <div style={{ fontWeight: 600, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Icons.Api /> 一键连通性测试
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px', gap: 12 }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Broker Host</label>
                <input className="form-input" value={probe.host} onChange={e => setProbe(p => ({ ...p, host: e.target.value }))} placeholder="如 47.113.200.150" />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Port</label>
                <input className="form-input" type="number" value={probe.port} onChange={e => setProbe(p => ({ ...p, port: e.target.value }))} />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>用户名</label>
                <input className="form-input" value={probe.username} onChange={e => setProbe(p => ({ ...p, username: e.target.value }))} />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>密码</label>
                <input className="form-input" type="password" value={probe.password} onChange={e => setProbe(p => ({ ...p, password: e.target.value }))} placeholder="留空则用平台已存密码" />
              </div>
            </div>
            <div className="form-group" style={{ marginTop: 12 }}>
              <label>探测 Topic</label>
              <input className="form-input" value={probe.topic} onChange={e => setProbe(p => ({ ...p, topic: e.target.value }))} />
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className="btn btn-primary" onClick={() => runProbe(false)} disabled={probing}>
                {probing ? '测试中…' : '平台侧探测'}
              </button>
              <button className="btn btn-outline" onClick={() => runProbe(true)} disabled={probing || !agent.available}>
                经 Agent 探测
              </button>
            </div>
            {probeResult && (
              <div style={{
                marginTop: 12, padding: 12, borderRadius: 8, fontSize: 13,
                background: probeResult.ok ? '#f0fdf4' : '#fef2f2',
                border: `1px solid ${probeResult.ok ? '#bbf7d0' : '#fecaca'}`,
                color: probeResult.ok ? '#166534' : '#991b1b',
              }}>
                <div style={{ fontWeight: 600 }}>{probeResult.ok ? '成功' : '失败'} · {probeResult.message}</div>
                {probeResult.detail && <div style={{ marginTop: 4, opacity: 0.9 }}>{probeResult.detail}</div>}
                <div style={{ marginTop: 4, opacity: 0.75 }}>
                  {probeResult.host}:{probeResult.port} · {probeResult.latency_ms != null ? `${probeResult.latency_ms}ms` : ''} · {probeResult.source || ''}
                </div>
              </div>
            )}
          </div>

          {/* 用户 / ACL */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, marginBottom: 16 }}>
            <div className="card">
              <div style={{ fontWeight: 600, marginBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span><Icons.Key /> MQTT 用户</span>
                <button className="btn btn-outline btn-xs" onClick={loadUsers} disabled={busy === 'users'}>加载</button>
              </div>
              <p style={{ fontSize: 12, color: 'var(--brand-text-muted)', marginBottom: 10 }}>
                解析 password_file；密码只写不读。经 Agent 调用 mosquitto_passwd。
              </p>
              {users.length > 0 && (
                <ul style={{ fontSize: 13, marginBottom: 12, paddingLeft: 18 }}>
                  {users.map(u => <li key={u.username}>{u.username}</li>)}
                </ul>
              )}
              <div className="form-group">
                <label>新增 / 改密 · 用户名</label>
                <input className="form-input" value={newUser.username} onChange={e => setNewUser(u => ({ ...u, username: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>密码</label>
                <input className="form-input" type="password" value={newUser.password} onChange={e => setNewUser(u => ({ ...u, password: e.target.value }))} />
              </div>
              <button className="btn btn-primary" onClick={saveUser} disabled={busy === 'saveUser'}>
                {busy === 'saveUser' ? '保存中…' : '保存并 reload'}
              </button>
            </div>

            <div className="card">
              <div style={{ fontWeight: 600, marginBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span><Icons.Shield /> ACL</span>
                <button className="btn btn-outline btn-xs" onClick={loadAcl} disabled={busy === 'acl'}>加载</button>
              </div>
              <textarea
                className="form-input"
                rows={12}
                value={acl}
                onChange={e => setAcl(e.target.value)}
                placeholder={"# 示例\nuser esop30\ntopic readwrite gridorbit/#\n"}
                style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12 }}
              />
              <button className="btn btn-primary" style={{ marginTop: 10 }} onClick={saveAcl} disabled={busy === 'saveAcl'}>
                {busy === 'saveAcl' ? '保存中…' : '校验保存并 reload'}
              </button>
            </div>
          </div>

          {/* Topic 约定 */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 600, marginBottom: 10 }}>Topic 约定（只读）</div>
            <table className="table" style={{ fontSize: 13 }}>
              <thead>
                <tr><th>用途</th><th>Pattern</th><th>QoS</th></tr>
              </thead>
              <tbody>
                {topics.map(t => (
                  <tr key={t.pattern}>
                    <td>{t.name}</td>
                    <td style={{ fontFamily: 'ui-monospace, monospace' }}>{t.pattern}</td>
                    <td>{t.qos || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(overview?.notes || []).map((n, i) => (
              <p key={i} style={{ fontSize: 12, color: 'var(--brand-text-muted)', marginTop: 8 }}>{n}</p>
            ))}
          </div>

          {logs && (
            <div className="card">
              <div style={{ fontWeight: 600, marginBottom: 8 }}>Mosquitto 日志（尾部）</div>
              <pre style={{
                fontSize: 11, maxHeight: 280, overflow: 'auto', margin: 0,
                background: '#0f172a0a', padding: 12, borderRadius: 8,
                whiteSpace: 'pre-wrap', wordBreak: 'break-all',
              }}>{logs}</pre>
            </div>
          )}
        </>
      )}
    </div>
  );
}
